/**
 * Legacy Timeline Builder
 *
 * Pure utility functions for deciding which timeline to use based on streaming state
 * and backend data. Replaces the complex merge logic in useChat.ts with a simpler rule:
 *
 * "Backend is canonical when not streaming"
 *
 * This eliminates duplicate entry bugs that occurred when:
 * 1. Refetch on window focus while local state exists (Bug 1)
 * 2. Concurrent stream + fetch race conditions (Bug 2)
 */

import type { TimelineEntry, SessionConversation } from '../types/types';

export interface BuildTimelineParams {
  localTimeline: TimelineEntry[];
  backendTimeline: TimelineEntry[];
  isStreaming: boolean;
  /** Local lastActivity timestamp (ms) for freshness comparison */
  localLastActivity?: number;
  /** Backend updated_at timestamp (ms) for freshness comparison */
  backendUpdatedAt?: number;
}

export interface BuildTimelineResult {
  timeline: TimelineEntry[];
  /** Which source was used: 'backend', 'local', or 'empty' */
  source: 'backend' | 'local' | 'empty';
}

/**
 * Grace period (ms) after recent local activity where local always wins over backend.
 * Prevents stale reads from DB secondary/replica nodes from overwriting fresh
 * streaming data before replication catches up.
 */
const POST_STREAM_GRACE_PERIOD_MS = 30_000;

/**
 * Builds the timeline to render based on streaming state and data availability.
 *
 * Rules:
 * 1. If streaming → use local timeline (preserve streaming state)
 * 2. If backend is empty or older → use local timeline (guard against data loss)
 * 2b. If local was recently active (within grace period) → use local (guard against replica lag)
 * 3. If not streaming and backend is valid → use backend (canonical source of truth)
 *
 * This simple approach prevents duplicate bugs by never merging - we pick one source.
 */
export function buildLegacyTimeline(
  params: BuildTimelineParams
): BuildTimelineResult {
  const {
    localTimeline,
    backendTimeline,
    isStreaming,
    localLastActivity,
    backendUpdatedAt,
  } = params;

  // Rule 1: If streaming, always use local timeline
  // This prevents mid-stream interference from refetches
  if (isStreaming) {
    return {
      timeline: localTimeline,
      source: 'local',
    };
  }

  // Rule 2: Guard against empty/older backend data
  // Don't wipe local data with empty backend (eventual consistency, race conditions)
  const backendHasMessages = backendTimeline.length > 0;
  const localHasMessages = localTimeline.length > 0;

  if (!backendHasMessages && localHasMessages) {
    // Backend is empty but local has data - keep local
    return {
      timeline: localTimeline,
      source: 'local',
    };
  }

  // Check if backend is older than local (freshness guard)
  // Only compare if both timestamps are available
  if (
    localLastActivity !== undefined &&
    backendUpdatedAt !== undefined &&
    localHasMessages &&
    backendUpdatedAt < localLastActivity
  ) {
    // Backend is older than local - keep local
    return {
      timeline: localTimeline,
      source: 'local',
    };
  }

  // Rule 2b: Grace period after recent local activity (e.g., stream just completed).
  // The backend uses secondaryPreferred reads for MongoDB, so after a stream
  // completes and data is saved to the primary node, subsequent reads may hit a
  // secondary that hasn't replicated yet. This returns stale data that would
  // overwrite the fresh local streaming timeline. The grace period ensures local
  // wins until replicas have caught up.
  if (
    localHasMessages &&
    localLastActivity !== undefined &&
    Date.now() - localLastActivity < POST_STREAM_GRACE_PERIOD_MS
  ) {
    return {
      timeline: localTimeline,
      source: 'local',
    };
  }

  // Rule 3: If not streaming and backend is valid, use backend
  if (backendHasMessages) {
    return {
      timeline: backendTimeline,
      source: 'backend',
    };
  }

  // Fallback: both empty
  if (localTimeline.length === 0 && backendTimeline.length === 0) {
    return {
      timeline: [],
      source: 'empty',
    };
  }

  // Default to local if we get here (shouldn't happen with above rules)
  return {
    timeline: localTimeline,
    source: 'local',
  };
}

/**
 * Finalizes a timeline by sorting and deduplicating entries.
 * This is a safety net for edge cases where the timeline might have
 * duplicates or be out of order.
 *
 * @param entries - Timeline entries to finalize
 * @returns Sorted and deduplicated timeline
 */
export function finalizeTimeline(entries: TimelineEntry[]): TimelineEntry[] {
  if (entries.length === 0) {
    return entries;
  }

  // Sort by position
  const sorted = [...entries].sort((a, b) => a.position - b.position);

  // Dedupe by position (keep first occurrence)
  // This handles edge cases where same position appears twice
  const seen = new Set<number>();
  const deduped = sorted.filter((entry) => {
    if (seen.has(entry.position)) {
      return false;
    }
    seen.add(entry.position);
    return true;
  });

  return deduped;
}

/**
 * Helper to check if a timeline has meaningful content.
 * A timeline is considered empty if it has no entries or only has error entries.
 */
export function hasTimelineContent(timeline: TimelineEntry[]): boolean {
  return timeline.some((entry) => entry.type !== 'error_item');
}

/**
 * Extracts streaming state info from SessionConversation for use with buildLegacyTimeline.
 */
export function isConversationStreaming(
  conversation: SessionConversation | undefined
): boolean {
  return conversation?.streamingState !== null && conversation?.streamingState !== undefined;
}
